import { Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { RowActionOption } from '@/components/table-rest-api/types';
import { SchemaItem } from '@/composables/custom-data/customData';
import type { ActionPayload } from './types';

/**
 * Interface for action component refs
 */
interface ActionComponentRef {
  open?: (payload: ActionPayload) => void;
}

/**
 * Options for useObjectsDataActions composable
 */
interface UseObjectsDataActionsOptions {
  deleteSchemaRef: Ref<ActionComponentRef | null>;
  apiReferenceRef: Ref<ActionComponentRef | null>;
  onEditSchema: (schemaItem: SchemaItem) => void;
  onActivateSchema: (schemaItem: SchemaItem) => void;
  onDeactivateSchema: (schemaItem: SchemaItem) => void;
}

/**
 * Composable that provides row actions for objects data list
 */
export default function useObjectsDataActions(options: UseObjectsDataActionsOptions) {
  const { t } = useI18n();
  const router = useRouter();

  /**
   * Row actions - triggered from individual row menu
   */
  const rowActions: RowActionOption[] = [
    // View data
    {
      label: t('myShopData.syncs.viewData'),
      icon: 'far fa-eye',
      command: (rowData: SchemaItem) => {
        router.push({
          name: `shop.syncs.${rowData.type}`,
          query: rowData.id_custom_object ? { id: rowData.id_custom_object } : undefined,
        });
      },
      visible: () => true,
    },
    // Edit schema
    {
      label: t('customData.syncList.editObject'),
      icon: 'fas fa-pencil-alt',
      command: (rowData: SchemaItem) => {
        options.onEditSchema(rowData);
      },
      visible: () => true,
    },
    // API Reference
    {
      label: t('myShopData.actions.apiReference'),
      icon: 'fas fa-code',
      command: (rowData: SchemaItem) => {
        if (options.apiReferenceRef.value?.open) {
          options.apiReferenceRef.value.open({ schemaItem: rowData });
        }
      },
      visible: () => true,
    },
    // Activate (inactive only)
    {
      label: t('myShopData.actions.activate'),
      icon: 'fas fa-play-circle',
      command: (rowData: SchemaItem) => {
        options.onActivateSchema(rowData);
      },
      visible: (rowData: SchemaItem) => rowData.active === false,
    },
    // Deactivate (active only)
    {
      label: t('myShopData.actions.deactivate'),
      icon: 'fas fa-pause-circle',
      command: (rowData: SchemaItem) => {
        options.onDeactivateSchema(rowData);
      },
      visible: (rowData: SchemaItem) => rowData.active === true,
    },
    // Separator
    {
      separator: true,
      label: '',
      command: () => { /* separator */ },
      visible: () => true,
    },
    // Delete
    {
      label: t('myShopData.actions.delete'),
      icon: 'far fa-trash-alt',
      class: 'p-button-danger',
      command: (rowData: SchemaItem) => {
        if (options.deleteSchemaRef.value?.open) {
          options.deleteSchemaRef.value.open({
            schemaItem: rowData,
          });
        }
      },
      visible: () => true,
    },
  ];

  return {
    rowActions,
  };
}
